/**
 * DocumentsSection (D2 Item 3) — the documents area of a lead.
 *
 * It replaces a flat list that had three problems: every row carried five or six
 * buttons, regenerating a document left a near-identical row beside the old one,
 * and nothing could be done to more than one document at a time.
 *
 * THREE IDEAS, in order of how much they change:
 *
 * 1. ROWS COLLAPSE BY KIND. A lead accumulates six Bills of Lading because
 *    someone regenerated after fixing an address, not because six exist. Only
 *    the current one of each kind is shown; the rest go behind an expander that
 *    says how many there are. See `supersededBy` for what "current" means and,
 *    more importantly, for what is never collapsed — a paid invoice or a
 *    completed contract is a record, not a stale draft.
 *
 * 2. ACTIONS MOVE INTO A MODAL. The row shows what a document IS (number, kind,
 *    date, status) and nothing else. Preview, Download PDF, Send, and the
 *    lifecycle actions live in DocumentModal, where there is room to label them
 *    honestly instead of compressing "Mark complete — this emails the client"
 *    into a 60px button.
 *
 * 3. SELECTION IS REAL. Checkboxes drive batch generate and batch send. Batch
 *    send is ONE email carrying a link to each document (the backend builds it);
 *    batch generate is a sequence of independent generates.
 *
 * WHAT EVERY BATCH RESULT MUST DO, per the D1 standing lesson: a batch where two
 * succeed and one fails says which failed and why. `BatchResult` has no state
 * that means both "fine" and "something went wrong" — `ok` and `failed` are
 * disjoint lists and a failure always carries a reason.
 */

import React, { useMemo, useState } from 'react';
import { useApiClient } from '../hooks/useApi';
// generated_at / sent_at are timestamptz. formatDate() is for DATE-only columns —
// it appends 'T00:00:00', which turns a full timestamp into NaN and renders '—'.
// The old documents list called formatDate on generated_at, so every row has been
// showing "Generated —" in production. recognition_date IS a date column, so it
// keeps formatDate.
import { formatDate, formatDateTime } from '../lib/utils';
import { formatDocNo } from '../lib/jobNumber';
import { Icons } from '../lib/icons';
import { DocumentWorkspace } from './DocumentWorkspace';
import { useAction } from '../hooks/useAction';

const DOC_LABEL: Record<string, string> = { work_order: 'Work Order', invoice: 'Invoice', bill_of_lading: 'Bill of Lading' };

export const docLabel = (d: any): string =>
  DOC_LABEL[d.doc_type] ?? (d.doc_type === 'template_document' ? (d.template_name || 'Document') : d.doc_type);

/**
 * The grouping key. Two Bills of Lading are the same kind; a Contract and a
 * Packing Standards document are not, even though both are template documents —
 * so template docs group by their template, not by `doc_type`. Falling back to
 * the template NAME when there is no id keeps documents generated before
 * template_id existed from collapsing into one bucket.
 */
const docKind = (d: any): string =>
  d.doc_type === 'template_document'
    ? `template:${d.template_id || d.template_name || d.id}`
    : d.doc_type;

const generatedAt = (d: any) => new Date(d.generated_at ?? 0).getTime();

/**
 * Is this document superseded by a newer one of the same kind?
 *
 * A document is superseded when a NEWER, non-void document of the same kind
 * exists AND this one is not itself a record worth keeping in view. The second
 * half matters more than the first:
 *
 *   - a PAID invoice is money that changed hands
 *   - a COMPLETED document is what the client signed
 *
 * Neither is made irrelevant by someone regenerating afterwards, and hiding
 * either one behind an expander would be the interface lying about what happened
 * on the job. Only drafts and plain sends collapse.
 */
function supersededBy(doc: any, all: any[]): boolean {
  if (doc.status === 'paid' || doc.completed_at) return false;
  return all.some((o) => o.id !== doc.id && docKind(o) === docKind(doc) && o.status !== 'void' && generatedAt(o) > generatedAt(doc));
}

interface Row { doc: any; superseded: any[] }

export interface DocumentsSectionProps {
  leadId: string;
  documents: any[];
  templates: any[];
  isAdmin: boolean;
  userName: (id: string | null | undefined) => string;
  refetch: () => void;
  refetchPayments: () => void;
  /**
   * Finalize (048) splits the list. 'documents' (Estimate tab): everything but
   * invoices, and the generate picker no longer offers an invoice — invoices are
   * issued from the Finalize draft. 'invoices' (Finalize tab): invoices only, no
   * generate picker at all.
   */
  scope: 'documents' | 'invoices';
}

type BatchResult = {
  title: string;
  ok: { label: string; detail?: string }[];
  failed: { label: string; reason: string }[];
  /**
   * NEITHER SUCCEEDED NOR FAILED. Regenerating supersedes the versions it
   * replaces, but it will not void one somebody has already signed — that is
   * admin-only on the manual path and an automatic supersede must not route
   * around it. The rep has to be TOLD, or an old signed version stays live on
   * the client's link and nothing on screen says so. Filing it under `failed`
   * would report a generation that worked as broken; leaving it out entirely is
   * the silent skip this phase exists to eliminate.
   */
  notes?: string[];
  onOverride?: () => void;
};

export function DocumentsSection({
  leadId, documents: allDocuments, templates, isAdmin, userName, refetch, refetchPayments, scope,
}: DocumentsSectionProps) {
  const api = useApiClient();
  const documents = useMemo(
    () => allDocuments.filter((d) => (d.doc_type === 'invoice') === (scope === 'invoices')),
    [allDocuments, scope],
  );

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set()); // kinds whose history is open
  const [showVoid, setShowVoid] = useState(false);
  const [genPicker, setGenPicker] = useState(false);
  const [genPick, setGenPick] = useState<Set<string>>(new Set()); // 'type:invoice' | 'template:<id>'
  const [busy, setBusy] = useState<string | null>(null);          // what is running, for the button label
  const [result, setResult] = useState<BatchResult | null>(null);
  const [modalDoc, setModalDoc] = useState<any>(null);
  const [workspaceDoc, setWorkspaceDoc] = useState<any>(null);

  const voided = useMemo(() => documents.filter((d) => d.status === 'void'), [documents]);

  // ---- Warnings: coverage out of step, and old live versions ---------------
  /**
   * Two ways a client ends up signing the wrong thing, both computed by the
   * server from the documents and the lead (GET /leads/:id/document-warnings):
   *   - a contract out for signature whose coverage section no longer matches
   *     the office's election (CLAUDE.md product rule) -> Regenerate contract;
   *   - an older version still live with client input after a regenerate
   *     (never voided automatically) -> Void the old one.
   * Re-checked whenever the documents change, and when coverage is saved.
   */
  const [warnings, setWarnings] = useState<{ coverage: any[]; stale: any[] } | null>(null);
  const loadWarnings = React.useCallback(() => {
    if (scope !== 'documents') return;
    api.getDocumentWarnings(leadId).then((r) => setWarnings(r.data ?? null)).catch(() => setWarnings(null));
  }, [api, leadId, scope]);
  React.useEffect(() => { loadWarnings(); }, [loadWarnings, allDocuments]);
  React.useEffect(() => {
    const on = (e: Event) => { if ((e as CustomEvent).detail?.leadId === leadId) loadWarnings(); };
    window.addEventListener('crm:coverage-changed', on);
    return () => window.removeEventListener('crm:coverage-changed', on);
  }, [leadId, loadWarnings]);

  const fixGuard = useAction();
  const regenerate = (w: any) => fixGuard.run(async (key) => {
    if (!w.template_id) return;
    setBusy('fix'); setResult(null);
    try {
      const res: any = await api.withIdempotencyKey(key).generateTemplateDocument(leadId, w.template_id);
      const num = res?.data?.number;
      const notes: string[] = [];
      for (const prev of (res?.data?.superseded ?? [])) notes.push(`${formatDocNo(prev.number)} was superseded by ${formatDocNo(num)} and is now void.`);
      for (const prev of (res?.data?.leftActive ?? [])) notes.push(`${formatDocNo(prev.number)} has client input and is still live — use "Void the old one" below.`);
      setResult({ title: `Regenerate ${w.label}`, ok: [{ label: w.label, detail: num ? formatDocNo(num) : undefined }], failed: [], notes });
    } catch (e: any) {
      setResult({ title: `Regenerate ${w.label}`, ok: [], failed: [{ label: w.label, reason: e?.message || 'Generation failed' }] });
    } finally {
      setBusy(null);
      refetch();
      loadWarnings();
    }
  });
  const voidOld = (w: any) => fixGuard.run(async () => {
    if (!window.confirm(`Void ${w.label} ${formatDocNo(w.number)}?\n\nIts client link stops working. ${formatDocNo(w.newer.number)} stays as the one to sign. This cannot be undone.`)) return;
    setBusy('fix');
    try {
      await api.updateDocumentStatus(w.id, 'void');
    } catch (e: any) {
      setResult({ title: `Void ${formatDocNo(w.number)}`, ok: [], failed: [{ label: w.label, reason: e?.message || 'Could not void it' }] });
    } finally {
      setBusy(null);
      refetch();
      loadWarnings();
    }
  });

  // Current-of-each-kind, newest first, each carrying its own superseded history.
  const rows: Row[] = useMemo(() => {
    const live = documents.filter((d) => d.status !== 'void');
    const shown = live.filter((d) => !supersededBy(d, live));
    const hidden = live.filter((d) => supersededBy(d, live));
    return shown
      .sort((a, b) => generatedAt(b) - generatedAt(a))
      .map((doc) => ({
        doc,
        superseded: hidden.filter((h) => docKind(h) === docKind(doc)).sort((a, b) => generatedAt(b) - generatedAt(a)),
      }));
  }, [documents]);

  // A row's superseded history is only reachable through its own expander, so
  // "select all" means the documents actually on screen.
  const visibleIds = useMemo(() => {
    const ids = rows.map((r) => r.doc.id);
    for (const r of rows) if (expanded.has(docKind(r.doc))) ids.push(...r.superseded.map((s) => s.id));
    if (showVoid) ids.push(...voided.map((v) => v.id));
    return ids;
  }, [rows, expanded, showVoid, voided]);

  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const selectedDocs = documents.filter((d) => selected.has(d.id));
  // A void document has no link, so it can never be part of a send. Saying so on
  // the button beats letting the user press Send and reading about it afterwards.
  const sendableDocs = selectedDocs.filter((d) => d.status !== 'void');

  const toggle = (id: string) =>
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const toggleAll = () =>
    setSelected((prev) => (allSelected ? new Set([...prev].filter((id) => !visibleIds.includes(id))) : new Set([...prev, ...visibleIds])));

  const toggleExpand = (kind: string) =>
    setExpanded((prev) => { const n = new Set(prev); n.has(kind) ? n.delete(kind) : n.add(kind); return n; });

  // ---- Batch generate ------------------------------------------------------
  /**
   * Each generate is an independent request, run in sequence so document numbers
   * come out in the order the user picked them. One failure does not abort the
   * rest — it is collected and reported by name, because "2 of 3 generated" with
   * no indication of WHICH one is missing is worse than useless on a job file.
   */
  // One generate batch / one send at a time (useAction), each with a key per
  // intent so a repeat cannot create or email twice.
  const genGuard = useAction();
  const sendGuard = useAction();
  const runBatchGenerate = () => genGuard.run((key) => runBatchGenerateNow(key));
  const runBatchGenerateNow = async (key: string) => {
    const picks = [...genPick];
    if (!picks.length) return;
    setBusy('generate'); setResult(null);
    const ok: { label: string; detail?: string }[] = [];
    const failed: { label: string; reason: string }[] = [];
    const notes: string[] = [];

    for (const [n, pick] of picks.entries()) {
      // One key per document in the batch (same scope, different documents).
      const keyed = api.withIdempotencyKey(`${key}-${n}`);
      const isTemplate = pick.startsWith('template:');
      const id = pick.slice(pick.indexOf(':') + 1);
      const label = isTemplate ? (templates.find((t) => t.id === id)?.name ?? 'Document') : (DOC_LABEL[id] ?? id);
      try {
        const res: any = isTemplate
          ? await keyed.generateTemplateDocument(leadId, id)
          : await keyed.generateDocument(leadId, id);
        const num = res?.data?.number;
        ok.push({ label, detail: num ? formatDocNo(num) : undefined });
        for (const prev of (res?.data?.superseded ?? [])) {
          notes.push(`${formatDocNo(prev.number)} was superseded by ${formatDocNo(num)} and is now void.`);
        }
        for (const prev of (res?.data?.leftActive ?? [])) {
          notes.push(`${formatDocNo(prev.number)} has signatures and was left active — void it manually if it's replaced.`);
        }
      } catch (e: any) {
        failed.push({ label, reason: e?.message || 'Generation failed' });
      }
    }

    setBusy(null);
    setGenPick(new Set());
    setGenPicker(false);
    refetch();
    setResult({ title: picks.length === 1 ? 'Generate' : `Generate ${picks.length} documents`, ok, failed, notes });
  };

  // ---- Batch send ----------------------------------------------------------
  /**
   * ONE email carrying a link to each document — the backend composes it, so the
   * client gets a single message rather than three that look like duplicates.
   *
   * The response separates documents that made it into the email from documents
   * that didn't (void, missing, or no link could be minted). A suppressed
   * recipient comes back as a 409 and becomes a "Send anyway" button rather than
   * a dead end, matching the single-document send.
   */
  const runBatchSend = (override = false) => sendGuard.run((key) => runBatchSendNow(override, key));
  const runBatchSendNow = async (override: boolean, key: string) => {
    const ids = sendableDocs.map((d) => d.id);
    if (!ids.length) return;
    setBusy('send'); setResult(null);
    const title = ids.length === 1 ? 'Send document' : `Send ${ids.length} documents in one email`;
    try {
      const res = await api.withIdempotencyKey(key).sendDocumentBatch(leadId, ids, override);
      const d = res.data!;
      refetch();
      if (!d.emailed) {
        const failed = (d.failed ?? []).map((f) => ({ label: f.label, reason: f.reason }));
        // "0 succeeded, 0 failed" would be a report that means nothing. If the
        // server said no email went out but named no document, the reason it
        // gave IS the finding — show that rather than an empty list.
        if (!failed.length) failed.push({ label: 'Nothing was sent', reason: d.reason || 'The server gave no reason.' });
        setResult({ title, ok: [], failed });
      } else {
        // WHICH ACT HAPPENED, not just that an email went. A batch containing
        // anything unsigned is sent as a signature REQUEST, and the rep needs
        // their confirmation to match what is now sitting in the client's
        // inbox — otherwise they follow up as if they had sent a copy.
        setResult({
          title: d.signatureRequest
            ? `${title} — sent for signature${d.isReminder ? ' (reminder)' : ''}`
            : title,
          ok: (d.sent ?? []).map((s) => ({
            label: s.label,
            detail: [formatDocNo(s.number), s.needsSignature ? 'sent for signature' : null].filter(Boolean).join(' · '),
          })),
          failed: (d.failed ?? []).map((f) => ({ label: f.label, reason: `${f.reason} — not included in the email` })),
        });
        setSelected(new Set());
      }
    } catch (e: any) {
      const labels = sendableDocs.map((d) => docLabel(d));
      setResult({
        title,
        ok: [],
        // The email is one message: if it fails, nothing was sent, and every
        // selected document has to say so rather than being left ambiguous.
        failed: labels.map((label) => ({ label, reason: e?.message || 'The email could not be sent' })),
        onOverride: e?.code === 'SUPPRESSED' ? () => runBatchSend(true) : undefined,
      });
    } finally {
      setBusy(null);
    }
  };

  const genOptions = [
    ...(['work_order', 'bill_of_lading'] as const).map((dt) => ({ key: `type:${dt}`, label: DOC_LABEL[dt] })),
    ...templates.map((t) => ({ key: `template:${t.id}`, label: `${t.name}${t.move_type === 'any' ? ' (any)' : ''}` })),
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '22px 0 12px', gap: '10px', flexWrap: 'wrap' }}>
        <h3 style={{ fontSize: '11px', fontWeight: 700, color: 'var(--crm-dim)', textTransform: 'uppercase' }}>{scope === 'invoices' ? 'Invoices' : 'Documents'}</h3>
        {scope === 'documents' && <div style={{ position: 'relative', display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button onClick={() => setGenPicker((v) => !v)} disabled={!!busy} style={primaryBtn}>
            + Generate{genPick.size ? ` (${genPick.size})` : ''}
          </button>

          {genPicker && (
            <>
              <div onClick={() => setGenPicker(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
              <div style={{ position: 'absolute', top: '32px', right: 0, zIndex: 41, width: '260px', background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '10px', padding: '10px', boxShadow: '0 16px 40px var(--crm-shadow-color)' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--crm-dim)', textTransform: 'uppercase', marginBottom: '8px' }}>
                  Generate — pick any number
                </div>
                <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                  {genOptions.map((o) => (
                    <label key={o.key} style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '6px 4px', fontSize: '12px', color: 'var(--crm-text)', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={genPick.has(o.key)}
                        onChange={() => setGenPick((prev) => { const n = new Set(prev); n.has(o.key) ? n.delete(o.key) : n.add(o.key); return n; })}
                      />
                      {o.label}
                    </label>
                  ))}
                  {!templates.length && (
                    <div style={{ fontSize: '11px', color: 'var(--crm-dim)', padding: '6px 4px' }}>
                      No document templates match this lead's move type.
                    </div>
                  )}
                </div>
                <button onClick={runBatchGenerate} disabled={!genPick.size || !!busy} style={{ ...primaryBtn, width: '100%', marginTop: '8px', opacity: genPick.size ? 1 : 0.5 }}>
                  {busy === 'generate' ? 'Generating…' : `Generate ${genPick.size || ''}`.trim()}
                </button>
              </div>
            </>
          )}
        </div>}
      </div>

      {/* Selection bar — only present when there is a selection to act on. */}
      {selected.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', padding: '9px 13px', marginBottom: '8px', background: 'var(--crm-accent-tint)', border: '1px solid var(--crm-accent-soft-line)', borderRadius: '9px' }}>
          <span style={{ fontSize: '12px', color: 'var(--crm-text)', fontWeight: 600 }}>{selected.size} selected</span>
          <button onClick={() => runBatchSend()} disabled={!sendableDocs.length || !!busy} style={{ ...primaryBtn, display: 'inline-flex', alignItems: 'center', gap: '6px', opacity: sendableDocs.length ? 1 : 0.5 }}>
            <Icons.send size={14} aria-hidden />
            {busy === 'send'
              ? 'Sending…'
              : sendableDocs.length === selected.size
                ? `Send ${sendableDocs.length === 1 ? 'as 1 email' : `${sendableDocs.length} in one email`}`
                : `Send ${sendableDocs.length} in one email (${selected.size - sendableDocs.length} void, skipped)`}
          </button>
          <button onClick={() => setSelected(new Set())} disabled={!!busy} style={ghostBtn}>Clear</button>
        </div>
      )}

      {/* Warnings — what to fix before a client signs the wrong thing. */}
      {scope === 'documents' && warnings && (warnings.coverage.length > 0 || warnings.stale.length > 0) && (
        <div role="alert" style={{ display: 'grid', gap: '8px', marginBottom: '10px' }}>
          {warnings.coverage.map((w) => (
            <div key={`cov-${w.id}`} style={warnBox}>
              <span style={{ flex: 1, minWidth: '220px', lineHeight: 1.5 }}>
                <strong>{w.label} {formatDocNo(w.number)}</strong>{w.out_for_signature ? ' is out for signature and' : ''}{' '}
                {w.contract_shows === 'locked'
                  ? 'shows Declared Value Protection locked in, but the file has no office election — the client cannot choose.'
                  : 'lets the client choose their coverage, but the office has elected Declared Value Protection on the file.'}
                {' '}Regenerate it so it matches.
              </span>
              <button onClick={() => regenerate(w)} disabled={!!busy || !w.template_id} style={warnBtn}>
                {busy === 'fix' ? 'Working…' : 'Regenerate contract'}
              </button>
            </div>
          ))}
          {warnings.stale.map((w) => (
            <div key={`old-${w.id}`} style={warnBox}>
              <span style={{ flex: 1, minWidth: '220px', lineHeight: 1.5 }}>
                <strong>{w.label} {formatDocNo(w.number)}</strong> is still live and has client input, although {formatDocNo(w.newer.number)} replaced it.
                {' '}The client could still sign the old one.
              </span>
              <button onClick={() => voidOld(w)} disabled={!!busy} style={{ ...warnBtn, color: 'var(--crm-red)', borderColor: 'var(--crm-red-soft-line)' }}>
                {busy === 'fix' ? 'Working…' : 'Void the old one'}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Batch outcome — never a bare "done". Successes and failures are listed
          separately and a failure always names the document and the reason. */}
      {result && <BatchReport result={result} busy={!!busy} onClose={() => setResult(null)} />}

      <div style={{ background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '10px', overflow: 'hidden' }}>
        {/* Header row: select-all + counts */}
        {(rows.length > 0 || voided.length > 0) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '8px 14px', borderBottom: '1px solid var(--crm-panel-2)', background: 'var(--crm-panel-2)' }}>
            <button onClick={toggleAll} title={allSelected ? 'Clear selection' : 'Select all shown'} aria-label={allSelected ? 'Clear selection' : 'Select all shown'} style={checkBtn}>
              {allSelected ? <Icons.checkboxOn size={16} /> : <Icons.checkboxOff size={16} />}
            </button>
            <span style={{ fontSize: '9px', fontWeight: 700, color: 'var(--crm-dim)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              Document
            </span>
            <span style={{ flex: 1 }} />
            {voided.length > 0 && (
              <button onClick={() => setShowVoid((v) => !v)} style={{ ...ghostBtn, fontSize: '10px' }}>
                {showVoid ? 'Hide' : 'Show'} {voided.length} void
              </button>
            )}
          </div>
        )}

        {rows.length === 0 && voided.length === 0 && (
          <div style={{ padding: '22px', textAlign: 'center', color: 'var(--crm-dim)', fontSize: '12px' }}>{scope === 'invoices' ? 'No invoices yet.' : 'No documents yet.'}</div>
        )}

        {rows.map((r) => {
          const kind = docKind(r.doc);
          const open = expanded.has(kind);
          return (
            <React.Fragment key={r.doc.id}>
              <DocRow
                doc={r.doc}
                selected={selected.has(r.doc.id)}
                onToggle={() => toggle(r.doc.id)}
                onOpen={() => setModalDoc(r.doc)}
                userName={userName}
              />
              {r.superseded.length > 0 && (
                <div style={{ padding: '0 14px 8px 47px', borderBottom: '1px solid var(--crm-panel-2)' }}>
                  <button onClick={() => toggleExpand(kind)} style={{ ...ghostBtn, display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10px', padding: '3px 8px' }}>
                    {open ? <Icons.chevronDown size={12} /> : <Icons.chevronRight size={12} />}
                    {open ? 'Hide' : 'Show'} {r.superseded.length} earlier {r.superseded.length === 1 ? 'version' : 'versions'}
                  </button>
                </div>
              )}
              {open && r.superseded.map((s) => (
                <DocRow
                  key={s.id}
                  doc={s}
                  superseded
                  selected={selected.has(s.id)}
                  onToggle={() => toggle(s.id)}
                  onOpen={() => setModalDoc(s)}
                  userName={userName}
                />
              ))}
            </React.Fragment>
          );
        })}

        {showVoid && voided.map((v) => (
          <DocRow key={v.id} doc={v} selected={selected.has(v.id)} onToggle={() => toggle(v.id)} onOpen={() => setModalDoc(v)} userName={userName} />
        ))}
      </div>

      {modalDoc && (
        <DocumentModal
          doc={modalDoc}
          leadId={leadId}
          isAdmin={isAdmin}
          userName={userName}
          onClose={() => setModalDoc(null)}
          onChanged={() => { refetch(); refetchPayments(); }}
          onOpenWorkspace={() => { setWorkspaceDoc(modalDoc); setModalDoc(null); }}
        />
      )}

      {workspaceDoc && <DocumentWorkspace doc={workspaceDoc} onClose={() => setWorkspaceDoc(null)} onChanged={refetch} />}
    </div>
  );
}

// ---- Row --------------------------------------------------------------------

/**
 * One document. It says what the document IS and carries no verbs beyond Open —
 * the old row had up to six buttons, which made every row look urgent and none
 * of them readable.
 */
function DocRow({
  doc, selected, superseded = false, onToggle, onOpen, userName,
}: {
  doc: any; selected: boolean; superseded?: boolean;
  onToggle: () => void; onOpen: () => void;
  userName: (id: string | null | undefined) => string;
}) {
  const isVoid = doc.status === 'void';
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: '11px',
        padding: superseded ? '8px 14px 8px 47px' : '10px 14px',
        borderBottom: '1px solid var(--crm-panel-2)',
        background: selected ? 'var(--crm-accent-tint)' : superseded ? 'var(--crm-panel-2)' : 'transparent',
        opacity: superseded || isVoid ? 0.62 : 1,
      }}
    >
      <button onClick={onToggle} aria-label={selected ? `Deselect ${docLabel(doc)}` : `Select ${docLabel(doc)}`} style={checkBtn}>
        {selected ? <Icons.checkboxOn size={16} color="var(--crm-accent)" /> : <Icons.checkboxOff size={16} />}
      </button>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '9px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '13px', fontWeight: 600, color: isVoid ? 'var(--crm-dim)' : 'var(--crm-text)', textDecoration: isVoid ? 'line-through' : 'none' }}>
            {docLabel(doc)}
          </span>
          <span style={{ fontSize: '11px', color: 'var(--crm-muted)', fontFamily: "'DM Mono', monospace" }}>{formatDocNo(doc.number)}</span>
        </div>
        <div style={{ fontSize: '10.5px', color: 'var(--crm-dim)', marginTop: '2px' }}>
          {formatDateTime(doc.generated_at)} · {userName(doc.generated_by)}
          {doc.sent_at && ` · sent ${formatDateTime(doc.sent_at)}`}
          {doc.doc_type === 'invoice' && doc.recognition_date && ` · recognizes ${formatDate(String(doc.recognition_date).slice(0, 10))}`}
        </div>
      </div>

      <StatusBadge doc={doc} />
      <button onClick={onOpen} style={rowBtn}>Open</button>
    </div>
  );
}

/**
 * The status badge reads the DOCUMENT, not just `status`, because `status` alone
 * cannot say "complete" or "emailed to the client" — and a contract that is
 * signed, complete and in the client's inbox showing a grey DRAFT pill is the
 * interface being wrong about the most important thing on the row.
 */
function StatusBadge({ doc }: { doc: any }) {
  const base: React.CSSProperties = { fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.5px', padding: '3px 9px', borderRadius: '999px', whiteSpace: 'nowrap' };
  if (doc.status === 'void') return <span style={{ ...base, background: 'var(--crm-red-tint)', color: 'var(--crm-red)' }}>VOID</span>;

  /**
   * A TEMPLATE DOCUMENT IS BADGED BY ITS SIGNING STATE, not by status.
   *
   * status answers "what happened to this as a business record" — and on a
   * document whose whole purpose is being signed, it answered the wrong
   * question in a way that read as an answer to the right one. SENT meant "we
   * emailed it" and covered awaiting, partially signed and
   * everything-filled-waiting-on-the-client alike; DRAFT covered both "ready to
   * send" and "this document has nothing to sign at all". A rep deciding
   * whether to chase someone could not tell those apart without opening each
   * document.
   *
   * The state is computed server-side by the one derivation (lib/signatureState)
   * and arrives on the row. Null means that read failed — the old status badge
   * comes back rather than a badge asserting something nobody verified.
   *
   * Invoices keep the status badge: ISSUED / SENT / PAID describe exactly what
   * happens to an invoice, and there is nothing on one to sign.
   */
  const sig = doc.signature_state;
  if (doc.doc_type === 'template_document' && sig) {
    const TONE: Record<string, { bg: string; fg: string }> = {
      neutral: { bg: 'var(--crm-panel-2)', fg: 'var(--crm-muted)' },
      info:    { bg: 'var(--crm-accent-tint)', fg: 'var(--crm-accent)' },
      warn:    { bg: 'var(--crm-amber-tint)', fg: 'var(--crm-amber)' },
      good:    { bg: 'var(--crm-green-tint)', fg: 'var(--crm-green)' },
      bad:     { bg: 'var(--crm-red-tint)', fg: 'var(--crm-red)' },
    };
    const toneOf: Record<string, keyof typeof TONE> = {
      unsignable: 'neutral', not_sent: 'neutral', awaiting: 'info',
      partially_signed: 'warn', signed_not_submitted: 'info',
      declined: 'bad', complete: 'good',
    };
    const t = TONE[toneOf[sig.status] ?? 'neutral'];
    const title = sig.status === 'complete' && doc.completion_email_sent_at
      ? 'Complete — emailed to the client'
      : sig.detail;
    return (
      <span title={title} style={{ ...base, background: t.bg, color: t.fg }}>
        {String(sig.label).toUpperCase()}
        {sig.status === 'complete' && doc.completion_email_sent_at ? ' · SENT' : ''}
      </span>
    );
  }

  if (doc.completed_at) {
    return (
      <span
        title={doc.completion_email_sent_at ? 'Complete — emailed to the client' : 'Complete — not emailed'}
        style={{ ...base, background: 'var(--crm-green-tint)', color: 'var(--crm-green)' }}
      >
        COMPLETE{doc.completion_email_sent_at ? ' · SENT' : ''}
      </span>
    );
  }
  const map: Record<string, { bg: string; fg: string; label: string }> = {
    draft: { bg: 'var(--crm-panel-2)', fg: 'var(--crm-muted)', label: 'DRAFT' },
    sent: { bg: 'var(--crm-accent-tint)', fg: 'var(--crm-accent)', label: 'SENT' },
    paid: { bg: 'var(--crm-green-tint)', fg: 'var(--crm-green)', label: 'PAID' },
  };
  // An invoice exists only once it is ISSUED (Finalize), so its 'draft' status
  // means "not emailed yet", not "unfinished" — badging it DRAFT read as though
  // the invoice could still change.
  if (doc.doc_type === 'invoice' && doc.status === 'draft') {
    return <span title="Issued — not emailed to the client yet" style={{ ...base, background: 'var(--crm-panel-2)', color: 'var(--crm-muted)' }}>ISSUED</span>;
  }
  const s = map[doc.status] ?? map.draft;
  return <span style={{ ...base, background: s.bg, color: s.fg }}>{s.label}</span>;
}

// ---- Batch report -----------------------------------------------------------

function BatchReport({ result, busy, onClose }: { result: BatchResult; busy: boolean; onClose: () => void }) {
  const allOk = result.failed.length === 0 && result.ok.length > 0;
  const border = allOk ? 'var(--crm-green-tint)' : result.ok.length ? 'var(--crm-amber-tint)' : 'var(--crm-red-tint)';
  return (
    <div style={{ border: `1px solid ${border}`, background: 'var(--crm-panel)', borderRadius: '9px', padding: '11px 13px', marginBottom: '9px', fontSize: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: result.ok.length || result.failed.length ? '7px' : 0 }}>
        <span style={{ fontWeight: 700, color: 'var(--crm-text)' }}>{result.title}</span>
        <span style={{ color: allOk ? 'var(--crm-green)' : result.ok.length ? 'var(--crm-amber)' : 'var(--crm-red)' }}>
          {result.ok.length} succeeded, {result.failed.length} failed
        </span>
        <span style={{ flex: 1 }} />
        {result.onOverride && (
          <button onClick={result.onOverride} disabled={busy} style={{ padding: '3px 10px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-amber-line)', color: 'var(--crm-amber)', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 600 }}>
            Send anyway
          </button>
        )}
        <button onClick={onClose} aria-label="Dismiss" style={{ ...checkBtn, color: 'var(--crm-dim)' }}><Icons.close size={14} /></button>
      </div>
      {result.ok.map((o, i) => (
        <div key={`ok${i}`} style={{ display: 'flex', gap: '7px', alignItems: 'center', color: 'var(--crm-green)', padding: '1.5px 0' }}>
          <Icons.check size={13} aria-hidden /> <span style={{ color: 'var(--crm-text)' }}>{o.label}</span>
          {o.detail && <span style={{ color: 'var(--crm-muted)', fontFamily: "'DM Mono', monospace", fontSize: '11px' }}>{o.detail}</span>}
        </div>
      ))}
      {result.failed.map((f, i) => (
        <div key={`no${i}`} style={{ display: 'flex', gap: '7px', alignItems: 'flex-start', color: 'var(--crm-red)', padding: '1.5px 0' }}>
          <Icons.warning size={13} aria-hidden style={{ marginTop: '2px', flexShrink: 0 }} />
          <span><span style={{ color: 'var(--crm-text)' }}>{f.label}</span> — {f.reason}</span>
        </div>
      ))}
      {/* Amber, not red: nothing went wrong, but something needs the rep's
          attention and will not chase them again. */}
      {(result.notes ?? []).map((n, i) => (
        <div key={`nt${i}`} style={{ display: 'flex', gap: '7px', alignItems: 'flex-start', color: 'var(--crm-amber)', padding: '1.5px 0' }}>
          <Icons.warning size={13} aria-hidden style={{ marginTop: '2px', flexShrink: 0 }} />
          <span style={{ color: 'var(--crm-text)' }}>{n}</span>
        </div>
      ))}
    </div>
  );
}

// ---- Per-document modal -----------------------------------------------------

/**
 * Everything you can do to ONE document, with room to say what each action does.
 *
 * Preview renders `rendered_content` in a sandboxed iframe — the SNAPSHOT, which
 * is what the client sees and what a PDF is made from, not a re-render from the
 * current template. Download PDF goes through the server route, so the file here
 * is byte-identical to the client's download rather than a browser print that
 * merely resembles it.
 */
function DocumentModal({
  doc, leadId, isAdmin, userName, onClose, onChanged, onOpenWorkspace,
}: {
  doc: any; leadId: string; isAdmin: boolean;
  userName: (id: string | null | undefined) => string;
  onClose: () => void; onChanged: () => void; onOpenWorkspace: () => void;
}) {
  const api = useApiClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string; onOverride?: () => void } | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const isVoid = doc.status === 'void';
  const isTemplateDoc = doc.doc_type === 'template_document';
  const label = docLabel(doc);

  // Every action in this modal is one-at-a-time (a second click is ignored).
  const guard = useAction();
  const run = (key: string, fn: () => Promise<void>) => guard.run(() => runNow(key, fn));
  const runNow = async (key: string, fn: () => Promise<void>) => {
    setBusy(key); setMsg(null);
    try {
      await fn();
    } catch (e: any) {
      // Some refusals carry a structured list of what is wrong — "Mark complete"
      // on a document with an empty block sends one entry per block. Showing the
      // summary alone would tell the user they are blocked without telling them
      // by what, which is the failure this phase keeps finding in other clothes.
      const details: any[] = Array.isArray(e?.details) ? e.details : [];
      const list = details.map((d) => `${d.label ?? d.kind}: ${d.reason}`).join('  ·  ');
      setMsg({ ok: false, text: list ? `${e.message}  —  ${list}` : (e?.message || 'That did not work — please try again.') });
    } finally { setBusy(null); }
  };

  const downloadPdf = () =>
    run('pdf', async () => {
      const { blob, filename } = await api.downloadDocumentPdf(doc.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      // Revoked on the next tick, not immediately: Safari cancels the download
      // if the object URL disappears before it starts reading.
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
      setMsg({ ok: true, text: `Downloaded ${filename}.` });
    });

  /**
   * The single-document send is the batch endpoint with ONE id: one composer,
   * one place to change the wording, and a suppressed recipient behaves the same
   * here as it does in a batch — a "Send anyway" prompt rather than a dead end.
   */
  const sendGuard = useAction();
  const sendToClient = (override = false) => sendGuard.run((key) => sendToClientNow(override, key));
  const sendToClientNow = async (override: boolean, key: string) => {
    setBusy('send'); setMsg(null);
    try {
      const res = await api.withIdempotencyKey(key).sendDocumentBatch(leadId, [doc.id], override);
      const d = res.data!;
      onChanged();
      if (d.emailed) setMsg({ ok: true, text: d.signatureRequest ? `${label} sent to the client for signature.` : `${label} emailed to the client.` });
      else setMsg({ ok: false, text: d.failed?.[0]?.reason ?? d.reason ?? 'Not sent.' });
    } catch (e: any) {
      setMsg({
        ok: false,
        text: e?.message || 'Send failed — please try again.',
        onOverride: e?.code === 'SUPPRESSED' ? () => sendToClient(true) : undefined,
      });
    } finally { setBusy(null); }
  };

  /**
   * WHICH DOCUMENTS THE MODAL CANNOT PREVIEW FROM THEIR SNAPSHOT.
   *
   * P4 moved the invoice's and the contractor work order's renderers into the
   * portal views and deleted the server-side layouts, so those documents store
   * a one-line placeholder as rendered_content. The modal read it and printed
   * it, so an invoice previewed as the sentence "Open this invoice from the
   * client portal." — which looks like a rendering failure and is, in effect,
   * one. Everything else snapshots real HTML and previews from it.
   */
  const previewsFromPortal = doc.doc_type === 'invoice' || doc.doc_type === 'contractor_work_order';

  /**
   * EVERY OTHER DOCUMENT PREVIEWS FILLED — the client's signatures, initials and
   * selections, and the completion certificate once complete — from the same
   * builder as the PDF (GET /documents/:id/preview). It used to show the blank
   * snapshot, so a COMPLETE contract looked unsigned in its own modal. The
   * snapshot stays the fallback while that loads or if it cannot.
   */
  const [filledHtml, setFilledHtml] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (previewsFromPortal || !doc.rendered_content) return;
    let alive = true;
    setFilledHtml(null);
    api.getDocumentPreview(doc.id)
      .then((r) => { if (alive) setFilledHtml(r.data?.html ?? null); })
      .catch(() => { /* the blank snapshot below is still a true preview of the text */ });
    return () => { alive = false; };
  }, [doc.id, doc.completed_at, previewsFromPortal, api]);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [previewErr, setPreviewErr] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!previewsFromPortal || isVoid) return;
    let alive = true;
    (async () => {
      try {
        // The same get-or-create link "Copy client link" uses — no second token
        // mechanism, and print=1 is the same flag the PDF route renders with.
        const res = await api.createDocumentPortalLink(doc.id);
        const url = res.data?.url;
        if (!alive) return;
        if (!url) { setPreviewErr('Could not open the client view for this document.'); return; }
        setPreviewUrl(`${url}${url.includes('?') ? '&' : '?'}print=1`);
      } catch (e: any) {
        // NEVER A BLANK FRAME. A failed preview says why; an empty white box
        // reads as a document with nothing in it.
        if (alive) setPreviewErr(e?.message || 'Could not open the client view for this document.');
      }
    })();
    return () => { alive = false; };
  }, [doc.id, previewsFromPortal, isVoid]);

  const copyLink = () =>
    run('link', async () => {
      const res = await api.createDocumentPortalLink(doc.id);
      const url = res.data!.url;
      setLink(url);
      try { await navigator.clipboard.writeText(url); setMsg({ ok: true, text: 'Client link copied to the clipboard.' }); }
      catch { setMsg({ ok: true, text: 'Client link ready — copy it from the box below.' }); }
    });

  const status = (action: 'sent' | 'paid' | 'void' | 'reopen', confirmText?: string) =>
    run(action, async () => {
      if (confirmText && !window.confirm(confirmText)) return;
      await api.updateDocumentStatus(doc.id, action);
      onChanged();
      onClose();
    });

  const markComplete = () =>
    run('complete', async () => {
      if (!window.confirm(`Mark "${label}" complete?\n\nThis emails the client a copy with the PDF attached. It is sent once — re-completing later will not send it again.`)) return;
      const res: any = await api.updateDocumentStatus(doc.id, 'complete');
      const d = res?.data ?? {};
      onChanged();
      setMsg(d.emailed
        ? { ok: true, text: 'Completed and emailed to the client with the PDF attached.' }
        : { ok: false, text: `Marked complete, but not emailed: ${d.emailNote || 'unknown reason'}.` });
    });

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'var(--crm-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' }}>
      {/* overflow:hidden so the white preview iframe is clipped by the modal's
          radius instead of squaring off its bottom corners. */}
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--crm-panel)', border: '1px solid var(--crm-line)', borderRadius: '12px', overflow: 'hidden', width: 'min(880px, 100%)', maxHeight: '100%', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 70px var(--crm-shadow-color)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px', borderBottom: '1px solid var(--crm-line)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--crm-text)' }}>
              {label} <span style={{ color: 'var(--crm-muted)', fontFamily: "'DM Mono', monospace", fontSize: '13px', fontWeight: 400 }}>{formatDocNo(doc.number)}</span>
            </div>
            <div style={{ fontSize: '11px', color: 'var(--crm-dim)', marginTop: '2px' }}>
              Generated {formatDateTime(doc.generated_at)} by {userName(doc.generated_by)}
              {doc.sent_at && ` · last sent ${formatDateTime(doc.sent_at)}`}
            </div>
          </div>
          <StatusBadge doc={doc} />
          <button onClick={onClose} aria-label="Close" style={{ ...checkBtn, color: 'var(--crm-muted)' }}><Icons.close size={18} /></button>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap', padding: '12px 16px', borderBottom: '1px solid var(--crm-line)' }}>
          <button onClick={downloadPdf} disabled={!!busy} style={{ ...rowBtn, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <Icons.download size={14} aria-hidden /> {busy === 'pdf' ? 'Generating…' : 'Download PDF'}
          </button>
          {!isVoid && (
            <button onClick={() => sendToClient()} disabled={!!busy} style={{ ...rowBtn, display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'var(--crm-accent)', borderColor: 'var(--crm-accent-soft-line)' }}>
              <Icons.send size={14} aria-hidden /> {busy === 'send' ? 'Sending…' : doc.sent_at ? 'Re-send to client' : 'Send to client'}
            </button>
          )}
          {!isVoid && (
            <button onClick={copyLink} disabled={!!busy} style={{ ...rowBtn, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <Icons.openExternal size={14} aria-hidden /> {busy === 'link' ? 'Working…' : 'Copy client link'}
            </button>
          )}
          {isTemplateDoc && !isVoid && (
            <button onClick={onOpenWorkspace} disabled={!!busy} style={{ ...rowBtn, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <Icons.signature size={14} aria-hidden /> Open workspace
            </button>
          )}

          <span style={{ flex: 1 }} />

          {doc.doc_type === 'invoice' && !isVoid && doc.status !== 'paid' && (
            <button onClick={() => status('paid')} disabled={!!busy} style={{ ...rowBtn, color: 'var(--crm-green)', borderColor: 'var(--crm-green-soft-line)' }}>Mark paid</button>
          )}
          {isTemplateDoc && !isVoid && (
            doc.completed_at
              // REOPEN IS NOT OFFERED ON A DOCUMENT THE CLIENT SIGNED. The
              // server refuses it outright; showing the button anyway meant
              // confirming a dialog and then being told no. client_signed is
              // computed on the list payload and fails closed — when it cannot
              // be determined the button is hidden rather than shown.
              ? (doc.client_signed
                  ? null
                  : <button onClick={() => status('reopen', 'Reopen this document?\n\nIt stops being final and its stored PDF is dropped. The client is NOT emailed again if it is completed a second time.')} disabled={!!busy} style={rowBtn}>Reopen</button>)
              : <button onClick={markComplete} disabled={!!busy} style={{ ...rowBtn, color: 'var(--crm-green)', borderColor: 'var(--crm-green-soft-line)' }}>{busy === 'complete' ? 'Completing…' : 'Mark complete'}</button>
          )}
          {!isVoid && !isTemplateDoc && doc.doc_type !== 'invoice' && doc.status === 'draft' && (
            <button onClick={() => status('sent')} disabled={!!busy} style={rowBtn}>Mark sent</button>
          )}
          {/* Voiding an INVOICE is admin-only (it is a money record); other
              document types stay open to any employee. */}
          {!isVoid && (doc.doc_type !== 'invoice' || isAdmin) && (
            <button onClick={() => status('void', 'Void this document? This cannot be undone.')} disabled={!!busy} style={{ ...rowBtn, color: 'var(--crm-red)', borderColor: 'var(--crm-red-soft-line)' }}>Void</button>
          )}
        </div>

        {msg && (
          <div style={{ padding: '9px 16px', fontSize: '12px', color: msg.ok ? 'var(--crm-green)' : 'var(--crm-red)', display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', borderBottom: '1px solid var(--crm-line)' }}>
            <span>{msg.text}</span>
            {msg.onOverride && (
              <button onClick={msg.onOverride} disabled={!!busy} style={{ padding: '3px 10px', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-amber-line)', color: 'var(--crm-amber)', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 600 }}>Send anyway</button>
            )}
          </div>
        )}

        {link && (
          <div style={{ padding: '9px 16px', borderBottom: '1px solid var(--crm-line)' }}>
            <input readOnly value={link} onFocus={(e) => e.currentTarget.select()} style={{ width: '100%', boxSizing: 'border-box', background: 'var(--crm-panel-2)', border: '1px solid var(--crm-line)', borderRadius: '6px', color: 'var(--crm-muted)', fontSize: '11px', padding: '6px 9px', fontFamily: "'DM Mono', monospace" }} />
          </div>
        )}

        {/* Preview. TWO SOURCES, because there are two kinds of document.
            srcDoc rather than a popup: a popup is blocked by default in most
            browsers and silently does nothing. */}
        <div style={{ flex: 1, minHeight: '260px', background: 'var(--crm-panel-2)', overflow: 'hidden' }}>
          {previewsFromPortal ? (
            /* PORTAL-BACKED: the invoice's renderer lives in the portal view, so
               rendered_content holds only the placeholder sentence "Open this
               invoice from the client portal" — which the modal was printing as
               though it were the document. This loads the real page in print
               mode, the same URL pdf.ts hands to Chromium, so the rep sees
               exactly what the client sees and there is still one renderer.

               THE SANDBOX IS RELAXED ONLY HERE. The portal is a React app on our
               own origin: it has to run scripts, and it has to keep its origin
               or its API calls fail CORS. A stored snapshot stays on sandbox=""
               — it is HTML we generated but never need to execute. */
            isVoid
              ? <div style={{ padding: '30px', textAlign: 'center', color: 'var(--crm-dim)', fontSize: '12px' }}>This document is void, so the client view is no longer available.</div>
              : previewErr
              ? <div style={{ padding: '30px', textAlign: 'center', color: 'var(--crm-red)', fontSize: '12px' }}>{previewErr}</div>
              : previewUrl
                ? <iframe
                    title={`${label} preview`}
                    src={previewUrl}
                    sandbox="allow-scripts allow-same-origin"
                    style={{ width: '100%', height: '100%', minHeight: '360px', border: 'none', background: 'var(--crm-white)' }}
                  />
                : <div style={{ padding: '30px', textAlign: 'center', color: 'var(--crm-dim)', fontSize: '12px' }}>Loading the client's view…</div>
          ) : doc.rendered_content
            ? <iframe title={`${label} preview`} sandbox="" srcDoc={filledHtml ?? doc.rendered_content} style={{ width: '100%', height: '100%', minHeight: '360px', border: 'none', background: 'var(--crm-white)' }} />
            : <div style={{ padding: '30px', textAlign: 'center', color: 'var(--crm-dim)', fontSize: '12px' }}>This document has no stored content to preview.</div>}
        </div>
      </div>
    </div>
  );
}

// ---- Shared styles ----------------------------------------------------------

const warnBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', padding: '9px 12px', fontSize: '12px', color: 'var(--crm-text)', background: 'var(--crm-amber-tint)', border: '1px solid var(--crm-amber-soft-line)', borderRadius: '9px' };
const warnBtn: React.CSSProperties = { padding: '5px 11px', background: 'var(--crm-panel)', color: 'var(--crm-amber)', border: '1px solid var(--crm-amber-line)', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap' };

const primaryBtn: React.CSSProperties = { padding: '6px 12px', background: 'var(--crm-accent-fill)', color: 'var(--crm-on-fill)', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap' };
const rowBtn: React.CSSProperties = { padding: '5px 11px', background: 'var(--crm-panel-2)', color: 'var(--crm-muted)', border: '1px solid var(--crm-line)', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', whiteSpace: 'nowrap' };
const ghostBtn: React.CSSProperties = { padding: '4px 9px', background: 'transparent', color: 'var(--crm-muted)', border: '1px solid var(--crm-line)', borderRadius: '6px', cursor: 'pointer', fontSize: '11px', whiteSpace: 'nowrap' };
const checkBtn: React.CSSProperties = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--crm-muted)', padding: 0, display: 'flex', alignItems: 'center' };
